import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useForeignHealthStore as useForeignHealthHiringStore } from '@/modules/foreign-health/hiring/stores/index'
import { useForeignHealthStore as useForeignHealthRaterStore } from '@/modules/foreign-health/rater/stores/index'
import { raterGuard } from './guards'
import type { RouteLocationNormalized, NavigationGuardNext, RouteRecordRaw } from 'vue-router'
import SpanishBranches from '@/modules/core/models/SpanishBranches'
import ModifyInsuranceModal from '@/modules/foreign-health/rater/views/modals/ModifyInsuranceModal.vue'
import BranchEnum from '@/modules/core/models/BranchEnum'
import ForeignHealthInsuranceProcessStepEnum from '@/modules/foreign-health/hiring/models/APP/ForeignHealthInsuranceProcessStepEnum'
import Step0Form from '@/modules/foreign-health/hiring/views/forms/Step0Form.vue'

const RATER_TITLE = 'Precios y Tarifas de Seguros de Salud para Extranjeros | ASISA'
const RATER_DESCRIPTION =
  'Calcula el precio de tu seguro de salud para extranjeros de ASISA. Tarifas adaptadas a tus necesidades en pocos pasos.'

export const FOREIGN_HEALTH_RATER_ROUTES = [
  {
    path: SpanishBranches.foreignHealth,
    name: 'rater-health-foreign',
    component: () => import('../modules/common/rater/views/BaseView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.FOREIGN_HEALTH,
      defaultBusinessLineId: 'AS02'
    },
    props: () => ({
      store: useForeignHealthRaterStore()
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing-almost-got-it')
      await raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.foreignHealth}/:productCode/:clientCode`,
    name: 'health-foreign-product',
    component: () => import('../modules/common/rater/views/ProductBaseView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.FOREIGN_HEALTH
    },
    props: () => ({
      store: useForeignHealthRaterStore(),
      modifyModal: ModifyInsuranceModal
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.foreignHealth}/propuestas`,
    name: 'health-foreign-insurance-proposals',
    component: () => import('@/modules/common/rater/views/ProductProposalsView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.FOREIGN_HEALTH
    },
    props: () => ({
      store: useForeignHealthRaterStore(),
      modifyModal: ModifyInsuranceModal
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.foreignHealth}/:id/ficha/:billingCycle`,
    name: 'health-foreign-product-view',
    component: () => import('@/modules/foreign-health/rater/views/ProductView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.FOREIGN_HEALTH
    },
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  }
] as RouteRecordRaw[]

export const FOREIGN_HEALTH_HIRING_ROUTES = [
  {
    path: `${SpanishBranches.foreignHealth}/:id`,
    name: 'hiring-health-foreign',
    component: () => import('@/modules/common/hiring/views/BaseView.vue'),
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.FOREIGN_HEALTH,
      defaultBusinessLineId: 'AV01'
    },
    props: () => ({
      store: useForeignHealthHiringStore(),
      branchName: BranchEnum.FOREIGN_HEALTH,
      repricingForm: Step0Form
    }),
    beforeEnter: (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing', 'common.loading.starting-hiring')
      raterGuard(to, from, next)
    }
  },
  {
    path: `${SpanishBranches.foreignHealth}/:id/pago-completado`,
    name: 'hiring-health-foreign-paid',
    meta: {
      title: 'Seguros Médicos ASISA'
    },
    component: () => import('../modules/common/hiring/views/SignatureCompleteView.vue'),
    props: {
      useStore: useForeignHealthRaterStore,
      noHash: true
    }
  },
  {
    path: `${SpanishBranches.foreignHealth}/firma/:hash`,
    name: 'health-foreign-hiring-signature',
    component: () => import('../modules/common/hiring/views/SignatureView.vue'),
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.FOREIGN_HEALTH
    },
    props: {
      branchName: BranchEnum.FOREIGN_HEALTH,
      stepCode: ForeignHealthInsuranceProcessStepEnum.SIGN
    },
    beforeEnter: () => {
      useLoadingStore().startLoading('common.loading.processing')
    }
  },
  {
    path: `${SpanishBranches.foreignHealth}/firma-completada/:hash`,
    name: 'health-foreign-signature-done',
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.FOREIGN_HEALTH
    },
    beforeEnter: () => {
      const loadingStore = useLoadingStore()
      loadingStore.stopLoading()
      loadingStore.startLoading('common.loading.processing')
    },
    component: () => import('../modules/common/hiring/views/SignatureCompleteView.vue'),
    props: {
      async emissionServiceCallback(quotationId: string) {
        return await useForeignHealthHiringStore().emission(quotationId)
      },
      branchName: BranchEnum.FOREIGN_HEALTH,
      info: 'copy_health_signature_view_info',
      info2: 'copy_health_signature_view_info2'
    }
  }
] as RouteRecordRaw[]
